import React, { useState, useEffect, useRef } from "react";
import styled, { keyframes } from "styled-components";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import EmojiPicker from "emoji-picker-react";
import { useAppDispatch } from "../../store";
import { createNotificationThunk } from "../../store/actions/admin.action";
import { message } from "antd";

interface NotificationFormData {
  title: string;
  body: string;
}

const schema = yup.object({
  title: yup
    .string()
    .required("Title is required")
    .min(3, "Title must be at least 3 characters"),
  body: yup.string().required("Body is required"),
});

const NotificationAdmin: React.FC = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showTitleEmojiPicker, setShowTitleEmojiPicker] = useState(false);
  const [showBodyEmojiPicker, setShowBodyEmojiPicker] = useState(false);
  const dispatch = useAppDispatch();

  const titleEmojiRef = useRef<HTMLDivElement>(null);
  const bodyEmojiRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<NotificationFormData>({
    resolver: yupResolver(schema),
  });

  const titleValue = watch("title");
  const bodyValue = watch("body");

  // Handle clicking outside emoji pickers
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        titleEmojiRef.current &&
        !titleEmojiRef.current.contains(event.target as Node)
      ) {
        setShowTitleEmojiPicker(false);
      }
      if (
        bodyEmojiRef.current &&
        !bodyEmojiRef.current.contains(event.target as Node)
      ) {
        setShowBodyEmojiPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const onEmojiClick = (emojiObject: any, field: "title" | "body") => {
    const currentValue = field === "title" ? titleValue : bodyValue;
    const element = document.getElementById(field) as
      | HTMLInputElement
      | HTMLTextAreaElement;
    const cursorPosition = element?.selectionStart || currentValue.length;

    const newValue =
      currentValue.slice(0, cursorPosition) +
      emojiObject.emoji +
      currentValue.slice(cursorPosition);
    setValue(field, newValue);

    // Set cursor position after emoji insertion
    setTimeout(() => {
      if (element) {
        const newCursorPosition = cursorPosition + emojiObject.emoji.length;
        element.setSelectionRange(newCursorPosition, newCursorPosition);
        element.focus();
      }
    }, 0);

    // Close the emoji picker
    if (field === "title") {
      setShowTitleEmojiPicker(false);
    } else {
      setShowBodyEmojiPicker(false);
    }
  };

  const onSubmit = async (data: NotificationFormData) => {
    setIsSubmitting(true);
    try {
      await dispatch(createNotificationThunk(data));
      reset();
      message.success("Notification created successfully");
    } catch (error) {
      console.error("Error submitting notification:", error);
      message.error("Failed to create notification");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Notification Admin</Title>
        <Subtitle>Create and manage notifications</Subtitle>
      </Header>

      <FormContainer>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <FormSection>
            <FormTitle>Create New Notification</FormTitle>

            <FormGroup>
              <Label htmlFor="title">Title *</Label>
              <InputContainer>
                <Input
                  id="title"
                  type="text"
                  placeholder="Enter notification title"
                  {...register("title")}
                  hasError={!!errors.title}
                />
                <EmojiPickerButton
                  type="button"
                  onClick={() => setShowTitleEmojiPicker(!showTitleEmojiPicker)}
                >
                  😊
                </EmojiPickerButton>
                {showTitleEmojiPicker && (
                  <EmojiPickerContainer ref={titleEmojiRef}>
                    <EmojiPicker
                      onEmojiClick={(emojiObject) =>
                        onEmojiClick(emojiObject, "title")
                      }
                      width={300}
                      height={400}
                    />
                  </EmojiPickerContainer>
                )}
              </InputContainer>
              {errors.title && (
                <ErrorMessage>{errors.title.message}</ErrorMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="body">Body *</Label>
              <InputContainer>
                <TextArea
                  id="body"
                  placeholder="Enter notification body content"
                  rows={6}
                  {...register("body")}
                  hasError={!!errors.body}
                />
                <EmojiPickerButton
                  type="button"
                  onClick={() => setShowBodyEmojiPicker(!showBodyEmojiPicker)}
                >
                  😊
                </EmojiPickerButton>
                {showBodyEmojiPicker && (
                  <EmojiPickerContainer ref={bodyEmojiRef}>
                    <EmojiPicker
                      onEmojiClick={(emojiObject) =>
                        onEmojiClick(emojiObject, "body")
                      }
                      width={300}
                      height={400}
                    />
                  </EmojiPickerContainer>
                )}
              </InputContainer>
              {errors.body && (
                <ErrorMessage>{errors.body.message}</ErrorMessage>
              )}
            </FormGroup>

            <ButtonGroup>
              <SubmitButton type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Creating..." : "Create Notification"}
              </SubmitButton>
            </ButtonGroup>
          </FormSection>
        </Form>
      </FormContainer>
    </Container>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Container = styled.div`
  padding: 24px;
  max-width: 800px;
  margin: 0 auto;
  animation: ${fadeIn} 0.3s ease-out;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
  color: white;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(90deg, #ff6b6b, #ff8e53);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: #9ca3af;
  margin: 0;
`;

const FormContainer = styled.div`
  background: rgba(45, 45, 50, 0.7);
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.05);
`;

const Form = styled.form`
  width: 100%;
`;

const FormSection = styled.div``;

const FormTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #fff;
  margin-bottom: 1.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: "";
    display: inline-block;
    width: 4px;
    height: 20px;
    background: linear-gradient(180deg, #ff6b6b, #ff8e53);
    border-radius: 2px;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-weight: 600;
  color: #fff;
  margin-bottom: 0.5rem;
  font-size: 0.95rem;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

const Input = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid
    ${(props) => (props.hasError ? "#ff4757" : "rgba(255, 255, 255, 0.1)")};
  border-radius: 8px;
  font-size: 1rem;
  transition: all 0.3s ease;
  background: rgba(30, 30, 35, 0.5);
  color: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(255, 71, 87, 0.1)" : "rgba(99, 102, 241, 0.1)"};
    background: rgba(30, 30, 35, 0.8);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover {
    border-color: ${(props) =>
      props.hasError ? "#ff4757" : "rgba(255, 255, 255, 0.2)"};
  }
`;

const TextArea = styled.textarea<{ hasError: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid
    ${(props) => (props.hasError ? "#ff4757" : "rgba(255, 255, 255, 0.1)")};
  border-radius: 8px;
  font-size: 1rem;
  font-family: inherit;
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s ease;
  background: rgba(30, 30, 35, 0.5);
  color: #fff;
  box-sizing: border-box;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px
      ${(props) =>
        props.hasError ? "rgba(255, 71, 87, 0.1)" : "rgba(99, 102, 241, 0.1)"};
    background: rgba(30, 30, 35, 0.8);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }

  &:hover {
    border-color: ${(props) =>
      props.hasError ? "#ff4757" : "rgba(255, 255, 255, 0.2)"};
  }
`;

const ErrorMessage = styled.span`
  color: #ff4757;
  font-size: 0.85rem;
  margin-top: 0.25rem;
  display: block;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  flex-wrap: wrap;
  justify-content: center;
`;

const SubmitButton = styled.button`
  background: linear-gradient(45deg, #ff6b6b, #ff8e53);
  color: #fff;
  border: none;
  padding: 0.8rem 1.5rem;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  position: relative;
  overflow: hidden;

  /* &:before {
    content: "";
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(
      120deg,
      transparent,
      rgba(255, 255, 255, 0.3),
      transparent
    );
    transition: 0.5s;
  } */

  &:hover {
    box-shadow: 0 6px 20px rgba(255, 107, 107, 0.4),
      0 0 20px rgba(255, 142, 83, 0.3);
    background: linear-gradient(45deg, #ff8e53, #ff6b6b);

    &:before {
      left: 100%;
    }
  }

  &:active {
    transform: translateY(-1px);
    box-shadow: 0 2px 10px rgba(255, 107, 107, 0.3);
  }
`;

const ResetButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  color: #9ca3af;
  border: 1px solid rgba(255, 255, 255, 0.1);
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: 120px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    color: #fff;
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const EmojiPickerButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px;
  cursor: pointer;
  font-size: 1.2rem;
  transition: all 0.3s ease;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  height: 44px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }

  &:active {
    transform: scale(0.95);
  }
`;

const EmojiPickerContainer = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  z-index: 1000;
  margin-top: 8px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

export default NotificationAdmin;
